import React, { useState, useEffect, useContext } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Card,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import Lottie from 'lottie-react';
import lottieAnimation from './lottie-animation.json';
import { ContextSession } from "../../services/ContextSession";

const ID_TYPES = {
  RTN: 'rtn',
  DNI: 'dni'
};

const GestionPartes = () => {
  const [valor, setValor] = useState('');
  const [tipoIdentificacion, setTipoIdentificacion] = useState('');
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  let sessi = useContext(ContextSession);

  useEffect(() => {
    if (tipoIdentificacion === ID_TYPES.RTN) {
      // Limpiar el formulario cuando se selecciona RTN
      setFormData({
        numInscripcion: '',
        nombres: '',
        primerApellido: '',
        segundoApellido: '',
        nombreCompleto: '',
        telefonocelular: '',
        correoelectronico: '',
        DescrBarrioResidencia: ''
      });
      setResultado(null); // Esto permitirá mostrar los campos vacíos para llenar manualmente
    }
  }, [tipoIdentificacion]);

  // Efecto para inicializar formData cuando hay resultado de DNI
  useEffect(() => {
    if (resultado && tipoIdentificacion === ID_TYPES.DNI) {
      const nombreCompleto = `${resultado.Nombres || ''} ${resultado.PrimerApellido || ''} ${resultado.SegundoApellido || ''}`.trim();

      // Limpiar el correo electrónico si contiene "ENR:"
      const correoLimpio = resultado.correoelectronico?.replace("ENR:", "").trim() || '';

      setFormData({
        numInscripcion: resultado.NumInscripcion || '',
        nombres: resultado.Nombres || '',
        primerApellido: resultado.PrimerApellido || '',
        segundoApellido: resultado.SegundoApellido || '',
        nombreCompleto: nombreCompleto,
        telefonocelular: resultado.telefonocelular || '',
        correoelectronico: correoLimpio,
        DescrBarrioResidencia: resultado.DescrBarrioResidencia || '',
      });
    }
  }, [resultado, tipoIdentificacion]);

  const validarIdentificacion = (input, type) => {
    const cleanedInput = input.replace(/\D/g, '');
    if (type === ID_TYPES.DNI) {
      return /^\d{13}$/.test(cleanedInput);
    }
    return false;
  };

  const formatInputValue = (value, type) => {
    const cleaned = value.replace(/\D/g, '');
    if (type === ID_TYPES.DNI) {
      return cleaned.slice(0, 13);
    }
    return cleaned;
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    const formattedValue = formatInputValue(inputValue, tipoIdentificacion);
    setValor(formattedValue);
  };

  const handleTipoChange = (e) => {
    const nuevoTipo = e.target.value;
    setTipoIdentificacion(nuevoTipo);
    setValor('');
    setResultado(null);
    setError('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const consultarIdentidad = async () => {
    setError('');
    setResultado(null);
    setLoading(true);

    const esValido = validarIdentificacion(valor, tipoIdentificacion);

    if (!esValido) {
      setError(`Número inválido. ${tipoIdentificacion === ID_TYPES.DNI ? 'DNI debe tener 13 dígitos' : 'RTN debe tener 14 dígitos'}.`);
      setLoading(false);
      return;
    }

    try {
      const token = sessi.sessionData.token; // Asegúrate de que sessi esté definido
      const usuarioId = sessi.sessionData.id;

      if (!usuarioId) {
        throw new Error('No se pudo identificar al usuario');
      }
       const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/rnp/datospersona/identidad/?identidad=${valor}&idUser=${usuarioId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Error en la consulta: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      setResultado(data);
      setEditMode(false);
    }
    catch (err) {
      setError(err.message || 'Ocurrió un error al consultar la identidad en el RNP.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      consultarIdentidad();
    }
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const handleSaveDialogOpen = () => {
    setOpenSaveDialog(true);
  };

  const handleSaveDialogClose = () => {
    setOpenSaveDialog(false);
  };

  const handleSaveData = () => {
    let apiData;

    if (tipoIdentificacion === ID_TYPES.DNI) {
      apiData = {
        identidad: formData.numInscripcion,
        NumInscripcion: formData.numInscripcion,
        Nombres: formData.nombres,
        PrimerApellido: formData.primerApellido,
        SegundoApellido: formData.segundoApellido,
        telefonocelular: formData.telefonocelular,
        correoelectronico: formData.correoelectronico,
        DescrBarrioResidencia: formData.DescrBarrioResidencia
      };
    } else if (tipoIdentificacion === ID_TYPES.RTN) {
      const nombreCompleto = formData.nombreCompleto.trim();
      const partesNombre = nombreCompleto.split(' ');

      const nombres = partesNombre.slice(0, -2).join(' ');
      const primerApellido = partesNombre[partesNombre.length - 2] || '';
      const segundoApellido = partesNombre[partesNombre.length - 1] || '';

      apiData = {
        identidad: formData.numInscripcion,
        NumInscripcion: formData.numInscripcion,
        Nombres: nombres,
        PrimerApellido: primerApellido,
        SegundoApellido: segundoApellido,
        telefonocelular: formData.telefonocelular,
        correoelectronico: formData.correoelectronico,
        DescrBarrioResidencia: formData.DescrBarrioResidencia
      };
    }

    console.log('Datos para API:', apiData);

    const token = sessi.sessionData.token; // Asegúrate de que sessi esté disponible
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    fetch('https://bei360.injupemp.gob.hn:8444/api/partes/guardar/', {
      method: 'POST',
      headers,
      body: JSON.stringify(apiData),
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw err; });
        }
        return response.json();
      })
      .then(data => {
        console.log('Respuesta de la API:', data);

        // Llamada adicional para registrar bitácora
        return fetch('https://bei360.injupemp.gob.hn:8444/api/consultas/registrar/', {
          method: 'POST',
          headers,
          body: JSON.stringify({ identidad: formData.numInscripcion }),
        });
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => { throw err; });
        }
        return response.json();
      })
      .then(data => {
        console.log('Bitácora registrada:', data);
        setSaveSuccess(true);
        setOpenSaveDialog(false);
        setEditMode(false);
      })
      .catch(error => {
        console.error('Error:', error);
        setError(error.error || 'Error al registrar la consulta o bitácora');
      });
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
  };

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      maxWidth: 650,
      margin: '0 auto',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
    }}>
      <Paper elevation={3} sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)'
      }}>
        {/* Encabezado con icono */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          gap: 2
        }}>
          <Box sx={{
            width: 70,
            height: 70,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            p: 1
          }}>
            <Lottie
              animationData={lottieAnimation}
              loop={true}
              autoplay={true}
              style={{ width: '200%', height: '200%' }}
            />
          </Box>
          <Typography variant="h4" fontWeight="bold" align="center" sx={{
            color: '#2c3e50',
            fontSize: { xs: '1.8rem', sm: '2.2rem' }
          }}>
            Crear Parte
          </Typography>
        </Box>

        {/* Tarjeta de búsqueda */}
        <Card sx={{
          maxWidth: 600,
          mb: 3, p: 2,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Typography variant="h6" sx={{
            mb: 2,
            color: '#34495e',
            fontWeight: '600'
          }}>
            Consultar información
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{
              color: '#34495e',
              fontWeight: '500',
              mb: 1
            }}>
              Tipo de identificación
            </FormLabel>
            <RadioGroup row value={tipoIdentificacion} onChange={handleTipoChange}>
              <FormControlLabel
                value={ID_TYPES.RTN}
                control={<Radio color="primary" />}
                label="RTN"
              />
              <FormControlLabel
                value={ID_TYPES.DNI}
                control={<Radio color="primary" />}
                label="DNI"
                sx={{ mr: 3 }}
              />
            </RadioGroup>
          </FormControl>

          {tipoIdentificacion && (
            tipoIdentificacion === ID_TYPES.RTN ? (
              <Alert severity="info" sx={{
                mb: 2,
                borderRadius: 2,
                backgroundColor: '#e8f4f8'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography>
                    Para consultar el RTN, por favor visite el sitio del SAR:{' '}
                    <Link
                      href="https://www.sar.gob.hn/registro-tributario-nacional-rtn/"
                      target="_blank"
                      rel="noopener"
                      sx={{ fontWeight: '500' }}
                    >
                      https://www.sar.gob.hn/registro-tributario-nacional-rtn/
                    </Link>
                  </Typography>
                </Box>
              </Alert>
            ) : tipoIdentificacion === ID_TYPES.DNI && (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Número de DNI"
                    variant="outlined"
                    fullWidth
                    value={valor}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Ingrese su número de DNI"
                    inputProps={{ maxLength: 13 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: '#dfe6e9',
                        },
                        '&:hover fieldset': {
                          borderColor: '#19438C',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    onClick={consultarIdentidad}
                    disabled={loading || !valor}
                    startIcon={!loading && <SearchIcon />}
                    sx={{
                      height: '45px',
                      width: '55%',
                      borderRadius: 2,
                      fontSize: '1rem',
                      fontWeight: '600',
                      backgroundColor: '#19438C',
                      color: 'white',
                      textTransform: 'none',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      '&:hover': {
                        backgroundColor: '#5ab4bf',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#e0e0e0',
                        color: '#9e9e9e'
                      }
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                        Procesando...
                      </Box>
                    ) : 'Buscar'}
                  </Button>
                </Grid>
              </Grid>
            )
          )}

        
        </Card>

        {(resultado || tipoIdentificacion === ID_TYPES.RTN) && (
          <Box mt={3}>
            {/* Encabezado de resultados */}
            <Box sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 3
            }}>
              <Typography variant="h5" sx={{
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '1.5rem'
              }}>
                Datos Personales
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Columna de datos */}
              <Grid item xs={12} md={12}>
                <Card sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                       <TextField
                          label={tipoIdentificacion === ID_TYPES.RTN ? "Número de RTN" : "DNI"}
                          fullWidth
                          value={formData.numInscripcion}
                          onChange={(e) => {
                            const onlyNumbers = e.target.value.replace(/\D/g, '');
                            if (onlyNumbers.length <= 14) {
                              setFormData(prev => ({
                                ...prev,
                                numInscripcion: onlyNumbers
                              }));
                            }
                          }}
                          name="numInscripcion"
                          margin="normal"
                          disabled={tipoIdentificacion !== ID_TYPES.RTN || !editMode}
                          inputProps={{
                            maxLength: 14,
                            inputMode: 'numeric',
                            pattern: '[0-9]*'
                          }}
                           
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiInputLabel-root': {
                              transform: 'translate(14px, 12px) scale(1)',
                              '&.Mui-focused': {
                                transform: 'translate(14px, -9px) scale(0.75)'
                              },
                              '&.MuiFormLabel-filled': {
                                transform: 'translate(14px, -9px) scale(0.75)'
                              }
                            }
                          }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Nombre Completo"
                        fullWidth
                        value={formData.nombreCompleto}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            nombreCompleto: e.target.value
                          }));
                        }}
                        name="nombreCompleto"
                        margin="normal"
                        disabled={tipoIdentificacion !== ID_TYPES.RTN || !editMode }
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            transform: 'translate(14px, 12px) scale(1)',
                            '&.Mui-focused': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            },
                            '&.MuiFormLabel-filled': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Teléfono"
                        fullWidth
                        value={formData.telefonocelular}
                        onChange={handleFormChange}
                        name="telefonocelular"
                        disabled={!editMode}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            transform: 'translate(14px, 12px) scale(1)',
                            '&.Mui-focused': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            },
                            '&.MuiFormLabel-filled': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Correo Electrónico"
                        type="email"
                        fullWidth
                        value={formData.correoelectronico}
                        onChange={handleFormChange}
                        name="correoelectronico"
                        disabled={!editMode}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            transform: 'translate(14px, 12px) scale(1)',
                            '&.Mui-focused': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            },
                            '&.MuiFormLabel-filled': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        label="Barrio/Colonia"
                        fullWidth
                        value={formData.DescrBarrioResidencia}
                        onChange={handleFormChange}
                        name="DescrBarrioResidencia"
                        margin="normal"
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiInputLabel-root': {
                            transform: 'translate(14px, 12px) scale(1)',
                            '&.Mui-focused': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            },
                            '&.MuiFormLabel-filled': {
                              transform: 'translate(14px, -9px) scale(0.75)'
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Botones de acción debajo de los campos */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant={editMode ? "outlined" : "contained"}
                          startIcon={editMode ? <CloseIcon /> : <EditIcon />}
                          onClick={toggleEditMode}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: '600',
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          {editMode ? 'Cancelar' : 'Editar'}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSaveDialogOpen}
                          color="success"
                          disabled={!editMode}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: '600',
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          Guardar
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Diálogo de confirmación de guardado */}
        <Dialog
          open={openSaveDialog}
          onClose={handleSaveDialogClose}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: '600',
            color: '#2c3e50',
            borderBottom: '1px solid #eee',
            pb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SaveIcon color="primary" />
              Confirmar Guardado
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Typography>¿Está seguro que desea guardar los cambios realizados?</Typography>
            <Box sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: 2
            }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Verifique que toda la información sea correcta antes de guardar.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleSaveDialogClose}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveData}
              color="success"
              variant="contained"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: '600'
              }}
            >
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notificación de guardado exitoso */}
        <Snackbar
          open={saveSuccess}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          >
            <Typography fontWeight="300">¡Datos guardados correctamente!</Typography>
          </Alert>
        </Snackbar>

        {/* Notificación de error */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setError('')} 
            severity="error"
            sx={{ 
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
            icon={<ErrorOutlineIcon fontSize="inherit" />}
          >
            <Typography fontWeight="300">{error}</Typography>
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
};

export default GestionPartes;